import { LoadingButton } from '@mui/lab';
import { Button, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { useUpdateBrand } from 'src/graphql/entities/brand';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { ClientSegmentDialogShell } from './client-segment-dialog-shell';
import { ClientSegmentFormFields } from './client-segment-form-fields';
import {
  ClientSegmentFormField,
  type ClientSegmentFormValues,
  DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
} from './client-segment-form.types';
import { useClientSegmentDeleteFlow } from './use-client-segment-delete-flow';

export const ClientSegmentEditModal = () => {
  const { isOpened, modalProps } = useModalState(Modals.ClientSegmentEditModal);
  const { closeModal } = useModalMethods(Modals.ClientSegmentEditModal);
  const { showSuccessAlert } = useNotifications();
  const { updateBrand } = useUpdateBrand();
  const { start: startDeleteFlow } = useClientSegmentDeleteFlow();

  const segment = modalProps?.segment ?? null;
  const sitesCount = modalProps?.sitesCount ?? 0;

  const values: ClientSegmentFormValues = useMemo(
    () => ({
      [ClientSegmentFormField.Name]: segment?.name ?? '',
      [ClientSegmentFormField.DisplayName]: segment?.displayName ?? '',
    }),
    [segment?.name, segment?.displayName],
  );

  const formMethods = useForm<ClientSegmentFormValues>({
    defaultValues: DEFAULT_CLIENT_SEGMENT_FORM_VALUES,
    values,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
    reset,
  } = formMethods;

  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const close = useCallback(() => {
    reset(DEFAULT_CLIENT_SEGMENT_FORM_VALUES);
    setSubmissionError(null);
    closeModal();
  }, [closeModal, reset]);

  const handleDelete = useCallback(() => {
    if (!segment) return;
    close();
    void startDeleteFlow({ segment, sitesCount });
  }, [segment, sitesCount, close, startDeleteFlow]);

  const onSubmit = handleSubmit(async (submitted) => {
    if (!segment) {
      setSubmissionError('No segment selected.');
      return;
    }

    const nameDirty = !!dirtyFields[ClientSegmentFormField.Name];
    const displayNameDirty = !!dirtyFields[ClientSegmentFormField.DisplayName];

    if (!nameDirty && !displayNameDirty) {
      close();
      return;
    }

    setSubmissionError(null);

    const result = await updateBrand({
      brandId: segment.brandId,
      ...(nameDirty ? { name: submitted[ClientSegmentFormField.Name].trim() } : {}),
      ...(displayNameDirty
        ? { displayName: submitted[ClientSegmentFormField.DisplayName].trim() }
        : {}),
    });

    if (!result) {
      setSubmissionError('Segment fields could not be updated. Please retry.');
      return;
    }

    showSuccessAlert('Client segment updated.');
    close();
  });

  if (!isOpened || !segment) return null;

  const subtitle = segment.brandGroup?.brandGroupName
    ? `${segment.name ?? ''} · ${segment.brandGroup.brandGroupName}`
    : (segment.name ?? undefined);

  const actions = (
    <>
      <Button color="error" onClick={handleDelete} disabled={isSubmitting}>
        Delete segment
      </Button>
      <Stack direction="row" spacing={1}>
        <Button onClick={close} disabled={isSubmitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Save changes
        </LoadingButton>
      </Stack>
    </>
  );

  return (
    <ClientSegmentDialogShell
      open
      title="Edit client segment"
      subtitle={subtitle}
      error={submissionError}
      actions={actions}
      onClose={close}
    >
      <GenericForm<ClientSegmentFormValues> formMethods={formMethods}>
        <ClientSegmentFormFields disabled={isSubmitting} />
      </GenericForm>
    </ClientSegmentDialogShell>
  );
};
